/**
 * MetricCardsSection Component
 * Displays 6 KPI cards with aggregate metrics for the Picks Data Dashboard
 * Design inspired by bias test dashboard (dark theme, semantic colors)
 * Supports both leg-level and parlay-level metrics
 */

import type { ParlayMetrics, PicksMetrics } from '@server/services/admin/adminPicksTypes.ts';
import { BarChart3, Clock, DollarSign, Layers, Target, TrendingUp } from 'lucide-react';

interface MetricCardsSectionProps {
  metrics: PicksMetrics | ParlayMetrics | null;
  loading: boolean;
}

export function MetricCardsSection({ metrics, loading }: MetricCardsSectionProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4 @5xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="animate-pulse rounded-lg border border-theme-border bg-theme-surface p-4"
          >
            <div className="h-4 w-16 rounded bg-theme-border"></div>
            <div className="mt-2 h-8 w-24 rounded bg-theme-border"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  // Check if this is ParlayMetrics (has totalParlays) or PicksMetrics (has totalPicks)
  const isParlayMetrics = 'totalParlays' in metrics;

  // Determine hit rate badge color (success if >= 50%, danger if < 50%)
  const hitRateColor = metrics.overallHitRate >= 50 ? 'text-theme-success' : 'text-theme-danger';

  if (isParlayMetrics) {
    const parlayMetrics = metrics as ParlayMetrics;

    return (
      <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4 @5xl:grid-cols-6">
        {/* Total Parlays */}
        <MetricCard
          icon={<Layers className="h-5 w-5 text-theme-text-secondary" />}
          label="Total Parlays"
          value={parlayMetrics.totalParlays.toLocaleString()}
          valueColor="text-theme-text"
        />

        {/* Hit Rate */}
        <MetricCard
          icon={<Target className="h-5 w-5 text-theme-text-secondary" />}
          label="Hit Rate"
          value={`${parlayMetrics.overallHitRate.toFixed(1)}%`}
          valueColor={hitRateColor}
          subtitle={`${parlayMetrics.hits}/${parlayMetrics.hits + parlayMetrics.misses} settled${parlayMetrics.noResult > 0 ? ` · ${parlayMetrics.noResult} no result` : ''}`}
        />

        {/* Avg Legs */}
        <MetricCard
          icon={<BarChart3 className="h-5 w-5 text-theme-text-secondary" />}
          label="Avg Legs"
          value={parlayMetrics.avgLegsPerParlay.toFixed(1)}
          valueColor="text-theme-text"
        />

        {/* Pending */}
        <MetricCard
          icon={<Clock className="h-5 w-5 text-theme-text-secondary" />}
          label="Pending"
          value={parlayMetrics.pending.toLocaleString()}
          valueColor="text-theme-warning"
        />

        {/* Avg Strategy Score */}
        <MetricCard
          icon={<BarChart3 className="h-5 w-5 text-theme-text-secondary" />}
          label="Avg Strategy"
          value={parlayMetrics.avgStrategyScore.toFixed(2)}
          valueColor="text-theme-text"
        />

        {/* Total Cost */}
        <MetricCard
          icon={<DollarSign className="h-5 w-5 text-theme-text-secondary" />}
          label="Total Cost"
          value={`$${parlayMetrics.totalCost.toFixed(2)}`}
          valueColor="text-theme-text"
          subtitle={`$${parlayMetrics.avgCostPerParlay.toFixed(3)}/parlay`}
        />
      </div>
    );
  }

  // PicksMetrics view
  const picksMetrics = metrics as PicksMetrics;

  // Determine over % color (success if 40-60%, warning if outside)
  const overPctInRange = picksMetrics.overPct >= 40 && picksMetrics.overPct <= 60;
  const overPctColor = overPctInRange ? 'text-theme-success' : 'text-theme-warning';

  return (
    <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4 @5xl:grid-cols-6">
      {/* Total Picks */}
      <MetricCard
        icon={<BarChart3 className="h-5 w-5 text-theme-text-secondary" />}
        label="Total Picks"
        value={picksMetrics.totalPicks.toLocaleString()}
        valueColor="text-theme-text"
      />

      {/* Hit Rate */}
      <MetricCard
        icon={<Target className="h-5 w-5 text-theme-text-secondary" />}
        label="Hit Rate"
        value={`${picksMetrics.overallHitRate.toFixed(1)}%`}
        valueColor={hitRateColor}
        subtitle={`${picksMetrics.hits}/${picksMetrics.hits + picksMetrics.misses} settled${picksMetrics.voids > 0 ? ` · ${picksMetrics.voids} void` : ''}`}
      />

      {/* Over % */}
      <MetricCard
        icon={<TrendingUp className="h-5 w-5 text-theme-text-secondary" />}
        label="Over %"
        value={`${picksMetrics.overPct.toFixed(1)}%`}
        valueColor={overPctColor}
        subtitle={`${picksMetrics.oversCount} overs, ${picksMetrics.undersCount} unders`}
      />

      {/* Pending */}
      <MetricCard
        icon={<Clock className="h-5 w-5 text-theme-text-secondary" />}
        label="Pending"
        value={picksMetrics.pending.toLocaleString()}
        valueColor="text-theme-warning"
      />

      {/* Avg Strategy Score */}
      <MetricCard
        icon={<BarChart3 className="h-5 w-5 text-theme-text-secondary" />}
        label="Avg Strategy"
        value={picksMetrics.avgStrategyScore.toFixed(2)}
        valueColor="text-theme-text"
      />

      {/* Total Cost */}
      <MetricCard
        icon={<DollarSign className="h-5 w-5 text-theme-text-secondary" />}
        label="Total Cost"
        value={`$${picksMetrics.totalCost.toFixed(2)}`}
        valueColor="text-theme-text"
        subtitle={`$${picksMetrics.avgCostPerPick.toFixed(3)}/pick`}
      />
    </div>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueColor: string;
  subtitle?: string;
}

function MetricCard({ icon, label, value, valueColor, subtitle }: MetricCardProps) {
  return (
    <div className="flex flex-col justify-between rounded-lg border border-theme-border bg-theme-surface p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-theme-text-secondary">{label}</span>
        {icon}
      </div>
      <div className="mt-2">
        <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
        {subtitle && <div className="mt-1 text-xs text-theme-text-tertiary">{subtitle}</div>}
      </div>
    </div>
  );
}
